import { observer } from 'mobx-react-lite';

import { cn } from '@/lib/utils';
import { settingsStore } from '@/stores/settings.store';
import { formatTime, formatTimeRange as formatTimeRangeFn } from '@/utils/date';

interface TimeProps {
  date: Date | string;
  className?: string;
}

const DAY_PERIOD_REGEX = /\s*([AP]\.?M\.?|[ap]\.?m\.?)\s*$/;

function splitDayPeriod(formatted: string): { main: string; period: string } {
  const match = formatted.match(DAY_PERIOD_REGEX);
  if (!match) return { main: formatted, period: '' };
  return {
    main: formatted.slice(0, match.index).trimEnd(),
    period: match[1],
  };
}

/**
 * Renders time respecting user's 12hr/24hr preference.
 * Automatically re-renders when preference changes.
 * Prefer this over calling formatTime() directly in React components.
 */
export const Time = observer(({ date, className }: TimeProps) => {
  void settingsStore.timeFormat;

  const dateObj = new Date(date);
  const isValid = !isNaN(dateObj.getTime());

  if (!isValid) return null;

  const { main, period } = splitDayPeriod(formatTime(date));

  return (
    <time
      dateTime={dateObj.toISOString()}
      className={cn('tabular-nums', className)}
    >
      {main}
      {period && (
        <>
          {' '}
          <span className="text-[0.85em] opacity-80">{period}</span>
        </>
      )}
    </time>
  );
});

interface TimeRangeProps {
  start: Date | string;
  end: Date | string;
  className?: string;
}

/**
 * Renders a time range respecting user's 12hr/24hr preference.
 * Automatically re-renders when preference changes.
 * Prefer this over calling formatTimeRange() directly in React components.
 */
export const TimeRange = observer(
  ({ start, end, className }: TimeRangeProps) => {
    void settingsStore.timeFormat;

    const startDate = new Date(start);
    const endDate = new Date(end);
    const isValid = !isNaN(startDate.getTime()) && !isNaN(endDate.getTime());

    if (!isValid) return null;

    return <span className={className}>{formatTimeRangeFn(start, end)}</span>;
  }
);
